import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EmberObject, { action, computed } from '@ember/object';
import { sum, mapBy } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';

export default class ItemLedgerSummaryController extends Controller {
  @action
  set_reportTitle(value) {
    this._reportTitle = value;
  }

  @service
  strings;

  @computed('model.[]')
  get data() {
    let data = EmberObject.create({});
    const reducer = (data, item) => {
      let ledgerId = item.get('ledger.id');
      if (isEmpty(ledgerId)) {
        // No Ledger to map this to - PIA?
        return data;
      }
      let ledgerItem = item.get('ledgerItem');
      if (isEmpty(data[ledgerId])) {
        // We haven't seen this ledger yet; add it to the map.
        data.set(
          ledgerId,
          EmberObject.create({
            ledger: item.get('ledger'),
            items: [],
            debit: 0,
            receipts: 0,
            otherCredits: 0,
            balance: 0,
            tax: 0,
          }),
        );
      }

      if (isEmpty(ledgerItem.get('id'))) {
        // If there's no Ledger Item, create a dummy one (per Ledger) called "Other".
        ledgerItem = EmberObject.create({
          id: 0,
          name: 'Other',
        });
      }

      let itemId = ledgerItem.get('id');
      if (isEmpty(data.get(ledgerId).items[itemId])) {
        // We haven't seen this Ledger Item yet; add it to the map.
        data[ledgerId].items[itemId] = EmberObject.create({
          item: ledgerItem,
          debit: 0,
          receipts: 0,
          otherCredits: 0,
          balance: 0,
          tax: 0,
        });
      }

      // Update both ledger and ledger item totals.
      data[ledgerId].items[itemId].incrementProperty(
        'debit',
        item.displayAmount,
      );
      data[ledgerId].items[itemId].incrementProperty('receipts', item.receipts);
      data[ledgerId].items[itemId].incrementProperty(
        'otherCredits',
        item.otherCredits,
      );
      data[ledgerId].items[itemId].incrementProperty('balance', item.balance);
      data[ledgerId].items[itemId].incrementProperty('tax', item.taxAmount);

      data[ledgerId].incrementProperty('debit', item.displayAmount);
      data[ledgerId].incrementProperty('receipts', item.receipts);
      data[ledgerId].incrementProperty('otherCredits', item.otherCredits);
      data[ledgerId].incrementProperty('balance', item.balance);
      data[ledgerId].incrementProperty('tax', item.taxAmount);

      return data;
    };

    data = this.model.reduce(reducer, data);

    return data;
  }

  @mapBy('model', 'displayAmount')
  debitAmounts;

  @sum('debitAmounts')
  debitTotal;

  @mapBy('model', 'receipts')
  receiptsAmounts;

  @sum('receiptsAmounts')
  receiptsTotal;

  @mapBy('model', 'otherCredits')
  otherCreditsAmounts;

  @sum('otherCreditsAmounts')
  otherCreditsTotal;

  @mapBy('model', 'balance')
  balanceAmounts;

  @sum('balanceAmounts')
  balanceTotal;

  @mapBy('model', 'taxAmount')
  taxAmounts;

  @sum('taxAmounts')
  taxTotal;

  @tracked _reportTitle = null;

  @computed('from', 'to', '_reportTitle')
  get reportTitle() {
    if (this._reportTitle !== null) {
      return this._reportTitle;
    }
    return `Line Items: Ledger Summary: ${this.from} - ${this.to}`;
  }
}
